/* eslint-disable no-console */
import { call, takeLatest, putResolve, select } from 'redux-saga/effects';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import {
  getPadChoicesFromHDMS,
  updateFetchedPadChoices,
  setPageError
} from '../actions';
import { configuratorChoices } from '../selectors/configurator';
import { APP_ACTION_CARPET_PADDING_NO_MATCH, logNewRelicError } from '../utils/newRelicActions';
import { getCurrentCarpetChoices, selectPrefetchedChoicesByHDMSSku } from '../selectors/carpetPad';
import safeSaga from './safeSaga';
import { findTaggedValue } from '../utils/tags/findTags';
import fetchHDMSAPIinfo from '../api/getCarpetPaddingApi';

// mock response is for store: 2706 && sku: 1000-509-513
const mockAPIresponse = [
  {
    skuNumber: '26',
    description: 'LifeProof 1/2" Carpet Pad',
    price: 10.89,
    choiceType: 'Recommended'
  },
  {
    skuNumber: '23',
    description: 'Prime Comfort 1/2" Carpet Pad',
    price: 10.63,
    choiceType: null
  },
  {
    skuNumber: '16',
    description: 'Traffic Master 6# 7/16" Carpet Pad',
    price: 7.13,
    choiceType: null
  },
  {
    skuNumber: '18',
    description: 'Step Ahead Nike 8# 7/16" Carpet Pad',
    price: 8.49,
    choiceType: null
  }
];

function findPadChoiceInAllChoices(allChoices, padChoice) {
  return find(Object.values(allChoices), (choice) => {
    const hdmssku = findTaggedValue(choice.tags, 'hdmssku');
    return hdmssku === padChoice.skuNumber;
  });
}

function parseConfiguratorChoicesForPads(choices, carpetPadData, storeId, searchText) {
  const baseUrl = window?.__EXPERIENCE_PROPS__?.expConfiguration?.['flooring-base-url'];
  const updatedPadChoices = {};
  const padSkus = [];
  const padSkusWithChoiceType = [];
  try {
    carpetPadData.forEach((padChoice, index) => {
      padSkus.push(padChoice.skuNumber);

      padSkusWithChoiceType.push({
        ...padChoice,
        choiceType: padChoice.choiceType || 'Standard',
        padUIOrder: padChoice.padUIOrder || index + 1
      });

      const foundChoice = findPadChoiceInAllChoices(choices, padChoice);
      if (foundChoice) {
        updatedPadChoices[padChoice.skuNumber] = {
          ...foundChoice,
          padChoiceType: padChoice.choiceType,
          padDescription: padChoice.description,
          padPrice: padChoice.price,
          padUIOrder: padChoice.padUIOrder || index + 1,
          padHDMSSku: padChoice.skuNumber,
        };

      } else {
        logNewRelicError(APP_ACTION_CARPET_PADDING_NO_MATCH, '/api/hdms/getCarpetPadding',
          null, baseUrl, { storeId, sku: padChoice?.skuNumber, description: padChoice?.description, searchText });
        console.log('not found', padChoice.skuNumber);
      }

    });
    return {
      padSkus,
      updatedPadChoices,
      padSkusWithChoiceType
    };
  } catch {
    return {};
  }

}

function* fetchPadChoicesFromHDMS(action) {
  const { searchText: carpetHdmsSku, storeId, searchText } = action.payload;
  const prefetchedCarpetChoices = yield select(getCurrentCarpetChoices);
  const prefetchedPadChoices = yield select(selectPrefetchedChoicesByHDMSSku(carpetHdmsSku));
  let carpetPadData = {};
  if (prefetchedPadChoices) {
    if (isEmpty(prefetchedPadChoices)) {
      yield putResolve(setPageError({ errorMsg: 'Prefetched carpet pad choices is empty' }));
    }
    carpetPadData = prefetchedCarpetChoices?.[carpetHdmsSku] || [];
  } else {
    try {
      const data = yield call(fetchHDMSAPIinfo, action.payload);
      if (data) { carpetPadData = data; }
      if (isEmpty(data)) {
        yield putResolve(setPageError({ errorMsg: `204 No Content: ${carpetHdmsSku}` }));
      }
    } catch (err) {
      yield putResolve(setPageError({ hasErrors: true, errorMsg: '500 Network Error: in getCarpetPadding' }));
      // TODO: window.newrelic.noticeError(`getCarpetPadding failed with ${err.message}`);
    }
  }
  const choices = yield select(configuratorChoices);
  const { updatedPadChoices, padSkus, padSkusWithChoiceType } = parseConfiguratorChoicesForPads(choices,
    carpetPadData,
    storeId, searchText);
  if (isEmpty(updatedPadChoices)) {
    yield putResolve(setPageError({ errorMsg: 'Carpet pad is not available' }));
  }
  yield putResolve(updateFetchedPadChoices({
    padChoices: { ...updatedPadChoices },
    padSkusByHDMSsku: { [action.payload.searchText]: padSkusWithChoiceType },
    compatiblePadSkus: padSkus
  }));
}

export function* fetchPadChoicesFromHDMSSaga() {
  yield takeLatest(getPadChoicesFromHDMS.toString(), safeSaga(fetchPadChoicesFromHDMS));
}
